import { PartnerProgramConfigPayload } from '@/@types/PartnerProgram';
import { Input } from '@/components/shared/Input/Input';
import { Label } from '@/components/shared/Label/Label';
import CommissionInput from '@/components/shared/CommissionInput/CommissionInput';
import CurrencyPriceInput from '@/components/shared/CurrencyPriceInput/CurrencyPriceInput';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { formatCurrency } from '@/lib/select-options';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useConfigurePartnerProgram } from '../hooks/useConfigurePartnerProgram';
import { useFetchPartnerProgram } from '../hooks/useFetchPartnerProgram';

interface PartnerProgramForm {
  is_enabled: boolean;
  commission_type: 'fixed' | 'percentage';
  commission_value: number;
  commission_currency: string;
  is_lifetime: boolean;
  duration_days: number | null;
}

interface Props {
  productId: string;
  onClose: () => void;
}

export default function SidePartnerConfig({ productId, onClose }: Props) {
  const { t } = useTranslation(['affiliates', 'common']);
  const { partnerProgram } = useFetchPartnerProgram(productId);
  const { configurePartnerProgram, isConfiguring } =
    useConfigurePartnerProgram(productId);
  const [formattedPrice, setFormattedPrice] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<PartnerProgramForm>({
    defaultValues: {
      is_enabled: false,
      commission_type: 'fixed',
      commission_value: 500,
      commission_currency: 'BRL',
      is_lifetime: true,
      duration_days: null,
    },
  });

  const commissionType = watch('commission_type');
  const isLifetime = watch('is_lifetime');
  const currency = watch('commission_currency');
  const commissionValue = watch('commission_value');

  useEffect(() => {
    if (partnerProgram) {
      reset({
        is_enabled: partnerProgram.is_enabled,
        commission_type: (partnerProgram.commission_type as 'fixed' | 'percentage') || 'fixed',
        commission_value: partnerProgram.commission_value || 500,
        commission_currency: partnerProgram.commission_currency || 'BRL',
        is_lifetime: partnerProgram.is_lifetime ?? true,
        duration_days: partnerProgram.duration_days,
      });
      if (partnerProgram.commission_type === 'fixed' && partnerProgram.commission_value) {
        setFormattedPrice(
          formatCurrency(partnerProgram.commission_value / 100, partnerProgram.commission_currency || 'BRL'),
        );
      }
    }
  }, [partnerProgram, reset]);

  useEffect(() => {
    if (commissionType === 'fixed') {
      setFormattedPrice(formatCurrency((commissionValue || 0) / 100, currency || 'BRL'));
    }
  }, [commissionValue, currency, commissionType]);

  const onSubmit = async (data: PartnerProgramForm) => {
    const payload: PartnerProgramConfigPayload = {
      is_enabled: data.is_enabled,
      commission_type: data.commission_type,
      commission_value: data.commission_value,
      commission_currency: data.commission_currency,
      is_lifetime: data.is_lifetime ?? true,
      duration_days: data.is_lifetime ? null : data.duration_days,
    };
    await configurePartnerProgram(payload);
    onClose();
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    const valueInCents = parseInt(rawValue || '0', 10);
    setFormattedPrice(formatCurrency(valueInCents / 100, currency || 'BRL'));
    setValue('commission_value', valueInCents, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  return (
    <section className="h-full flex flex-col">
      <div className="p-6 flex-shrink-0">
        <h2 className="text-foreground font-semibold">
          {t('affiliates:partner_program_title')}
        </h2>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col flex-1 overflow-y-auto"
      >
        <section className="flex flex-col gap-4 px-6">
          <div>
            <p className="text-sm font-semibold leading-[24px] mb-4">
              {t('affiliates:commission_config_title')}
            </p>

            <div className="mb-4">
              <Label className="mb-2">{t('affiliates:commission_type_label')}</Label>
              <Controller
                control={control}
                name="commission_type"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={(val) => {
                      field.onChange(val);
                      clearErrors('commission_value');
                      if (val === 'fixed') {
                        setValue('commission_value', 500);
                        setFormattedPrice(formatCurrency(5, currency || 'BRL'));
                      } else {
                        setValue('commission_value', 1000);
                      }
                    }}
                    className="flex items-center gap-4 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="percentage" id="pp-percentage" />
                      <Label htmlFor="pp-percentage">
                        {t('affiliates:commission_type_percentage')}
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="fixed" id="pp-fixed" />
                      <Label htmlFor="pp-fixed">
                        {t('affiliates:commission_type_fixed')}
                      </Label>
                    </div>
                  </RadioGroup>
                )}
              />
            </div>

            {commissionType === 'percentage' ? (
              <CommissionInput
                label={t('affiliates:partner_commission_label')}
                helperText={errors.commission_value?.message}
                integerOnly
                minValue={1}
                maxValue={10000}
                defaultValue={partnerProgram?.commission_value ?? 1000}
                onChangeCommission={(value) =>
                  setValue(
                    'commission_value',
                    Number.parseInt(value || '0', 10),
                  )
                }
                dataCy="input-partner-commission-pct"
                {...register('commission_value', {
                  required: t('common:required_field'),
                  validate: {
                    min: (v) => v >= 1 || t('affiliates:commission_min'),
                    max: (v) => v <= 10000 || t('affiliates:commission_max'),
                  },
                })}
              />
            ) : (
              <CurrencyPriceInput
                formattedPrice={formattedPrice}
                onPriceChange={handlePriceChange}
                currency={currency || 'BRL'}
                onCurrencyChange={(value) => setValue('commission_currency', value)}
                currencies={['BRL', 'USD']}
                label={t('affiliates:partner_commission_label')}
                placeholder={t('affiliates:value_placeholder')}
                dataCy="input-partner-commission-fixed"
              />
            )}
            {errors.commission_value && (
              <p className="text-red-500 text-sm mt-1">
                {errors.commission_value.message}
              </p>
            )}
          </div>

          <div>
            <Label className="mb-2">{t('affiliates:duration_label')}</Label>
            <Controller
              control={control}
              name="is_lifetime"
              render={({ field }) => (
                <RadioGroup
                  value={field.value ? 'lifetime' : 'period'}
                  onValueChange={(val) => {
                    field.onChange(val === 'lifetime');
                    if (val === 'lifetime') {
                      setValue('duration_days', null);
                    }
                  }}
                  className="flex items-center gap-4 mt-2"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="lifetime" id="pp-lifetime" />
                    <Label htmlFor="pp-lifetime">
                      {t('affiliates:duration_lifetime')}
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="period" id="pp-period" />
                    <Label htmlFor="pp-period">
                      {t('affiliates:duration_period')}
                    </Label>
                  </div>
                </RadioGroup>
              )}
            />
            {!isLifetime && (
              <div className="mt-3 w-[200px]">
                <Input
                  type="number"
                  label={t('affiliates:duration_days_label')}
                  placeholder="90"
                  {...register('duration_days', {
                    required: !isLifetime ? t('common:required_field') : false,
                    valueAsNumber: true,
                    min: { value: 1, message: t('affiliates:duration_min') },
                  })}
                  helperText={errors.duration_days?.message}
                  data-cy="input-partner-duration-days"
                />
              </div>
            )}
          </div>
        </section>

        <div className="flex-1" />

        <div className="flex items-center justify-end p-4 border-t max-sm:pb-12">
          <Button
            type="button"
            variant="ghost"
            onClick={onClose}
            data-cy="btn-cancel-partner-config"
          >
            {t('affiliates:cancel')}
          </Button>
          <Button
            type="submit"
            disabled={isConfiguring}
            data-cy="btn-save-partner-program"
          >
            {isConfiguring ? t('affiliates:saving') : t('affiliates:save_changes')}
          </Button>
        </div>
      </form>
    </section>
  );
}
